import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Boxes, Brain, Copy, Download, KeyRound, Laptop, Plug, SlidersHorizontal, Trash2, Wrench } from 'lucide-react'
import { ApiKeysSettings } from '@/pages/ApiKeysSettings'
import { GeneralSettings } from '@/pages/GeneralSettings'
import { api } from '@/services/api'
import { connectAccount } from '@/services/account'
import { isDesktopApp } from '@/services/platform'
import type { AgentDevice, Memory, ModelEntry, ToolInfo } from '@/services/types'

type Tab = 'general' | 'devices' | 'models' | 'apiKeys' | 'memory' | 'tools'

/** Each tab, with the line that says what it is for. */
const TABS: {
  key: Tab
  label: string
  short: string
  icon: typeof Laptop
  blurb: string
  /**
   * Hidden inside the desktop app. Paired devices are a browser concern: in
   * the app the only device that matters is the one it is running on, and that
   * is connected by signing in.
   */
  browserOnly?: boolean
}[] = [
  {
    key: 'general',
    label: 'General',
    short: 'General',
    icon: SlidersHorizontal,
    blurb: 'How NevTan behaves on this machine.',
  },
  {
    key: 'devices',
    label: 'Devices',
    short: 'Devices',
    icon: Laptop,
    blurb: 'Machines NevTan can operate on your behalf.',
    browserOnly: true,
  },
  {
    key: 'models',
    label: 'Models',
    short: 'Models',
    icon: Boxes,
    blurb: 'Which models are available, and which one answers by default.',
  },
  {
    key: 'apiKeys',
    label: 'API keys',
    short: 'Keys',
    icon: KeyRound,
    blurb: 'Keys for using NevTan models from editors and other tools.',
  },
  {
    key: 'memory',
    label: 'Memory',
    short: 'Memory',
    icon: Brain,
    blurb: 'What NevTan has remembered about you across conversations.',
  },
  {
    key: 'tools',
    label: 'Tools & permissions',
    short: 'Tools',
    icon: Wrench,
    blurb: 'What NevTan may do, and what it must ask about first.',
  },
]

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('general')
  // Sign out lives in the account menu in the header, not here.
  const tabs = TABS.filter((item) => !(item.browserOnly && isDesktopApp))
  const active = tabs.find((item) => item.key === tab) ?? tabs[0]

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="mb-6">
          <h1 className="text-[1.6rem] font-extrabold tracking-display">Settings</h1>
          <p className="mt-1 text-[13.5px] text-content-muted">
            How NevTan runs on your machines, and what it is allowed to do.
          </p>
        </header>

        {/* Segmented rather than underlined: on a settings screen the tabs are
            a control, and a filled pill reads as one at a glance. */}
        <div className="mb-5 flex gap-1 rounded-xl border border-border bg-surface-raised p-1">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              aria-current={tab === item.key}
              className={clsx(
                'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[12.5px] font-semibold transition sm:flex-initial sm:shrink-0 sm:px-3 sm:text-[13px]',
                tab === item.key
                  ? 'bg-surface text-content shadow-card'
                  : 'text-content-muted hover:text-content',
              )}
            >
              {/* The icons are the first thing to go when the tabs have to
                  share 360px — the words are what identify them. */}
              <item.icon size={14} className="hidden sm:block" />
              <span className="sm:hidden">{item.short}</span>
              <span className="hidden sm:inline">{item.label}</span>
            </button>
          ))}
        </div>

        <p className="mb-5 text-[13px] text-content-muted">{active.blurb}</p>

        {tab === 'general' && <GeneralSettings />}
        {tab === 'devices' && !isDesktopApp && <DevicesTab />}
        {tab === 'models' && <ModelsTab />}
        {tab === 'apiKeys' && <ApiKeysSettings />}
        {tab === 'memory' && <MemoryTab />}
        {tab === 'tools' && <ToolsTab />}
      </div>
    </div>
  )
}

/** A titled panel. Every settings section is one of these. */
function Panel({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-surface p-4 sm:p-5">
      <h2 className="text-[15px] font-bold">{title}</h2>
      {description && (
        <p className="mt-1 text-[13px] leading-relaxed text-content-muted">{description}</p>
      )}
      <div className="mt-4">{children}</div>
    </section>
  )
}

function DevicesTab() {
  const [agents, setAgents] = useState<AgentDevice[]>([])
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [pairError, setPairError] = useState<string | null>(null)
  /** Device awaiting confirmation before removal; removal cannot be undone. */
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null)

  const reload = () => void api.listAgents().then(setAgents)
  useEffect(reload, [])

  const pair = async () => {
    setPairing(null)
    setPairError(null)
    // Inside the app, this machine is the one being connected, so it can do
    // the whole thing: ask, open the browser, and store what comes back. A
    // code to type would be asking someone to carry a value between two
    // windows on the same computer.
    if (isDesktopApp) {
      setConnecting(true)
      try {
        await connectAccount(() => undefined)
        reload()
      } catch (e) {
        setPairError(e instanceof Error ? e.message : 'Could not connect this device.')
      } finally {
        setConnecting(false)
      }
      return
    }
    // In a browser the machine being connected is somewhere else, so it needs
    // a code to carry there.
    setPairing(await api.startPairing())
    reload()
  }

  return (
    <div className="space-y-6">
      <Panel
        title="Pair a new device"
        description={
          isDesktopApp
            ? 'Connect this machine so models download and run on it.'
            : 'Get a code here, then enter it on the machine you want NevTan to operate.'
        }
      >
        <button
          type="button"
          onClick={() => void pair()}
          disabled={connecting}
          className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-content transition hover:opacity-90 disabled:opacity-60 sm:w-auto"
        >
          {connecting ? 'Waiting for approval…' : 'Connect device'}
        </button>
        {pairError && (
          <p className="mt-2 text-sm text-danger">{pairError}</p>
        )}

        {pairing && (
          <div className="mt-3 rounded-lg border border-accent/40 bg-accent/5 p-4 text-sm">
            <p className="font-medium">Enter this code on the device you want to connect.</p>
            <p className="mt-3 select-all text-center font-mono text-3xl tracking-[0.3em] text-accent">
              {pairing.code}
            </p>
            <p className="mt-3 text-xs text-content-muted">
              On that machine, run:
            </p>
            <pre className="mt-1 overflow-x-auto rounded bg-surface-sunken p-3 font-mono text-xs">
              {`nevtan-agent pair --code ${pairing.code}`}
            </pre>
            <p className="mt-2 text-xs text-content-muted">
              The code works once and expires in ten minutes. The device names itself.
            </p>

            {/*
              The command assumes NevTan is already on that machine. Someone
              pairing their first device has nothing to run it with, and the
              page gave them no way forward -- so the install is offered here
              rather than left to be discovered.
            */}
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs text-content-muted">
                Not installed on that machine yet?
              </p>
              <a
                href="/#download"
                className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
              >
                <Download size={12} /> Download NevTan AI
              </a>
            </div>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(pairing.code)}
              className="mt-2 flex items-center gap-1.5 text-xs text-accent hover:underline"
            >
              <Copy size={12} /> Copy code
            </button>
          </div>
        )}
      </Panel>

      <Panel title="Paired devices">
        {agents.length === 0 ? (
          <p className="text-sm text-content-muted">No devices paired yet.</p>
        ) : (
          <ul className="space-y-2">
            {agents
              .filter((agent) => agent.status !== 'REVOKED')
              .map((agent) => (
              // Wraps at narrow widths so the actions drop below the details
              // instead of colliding with them.
              <li
                key={agent.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-surface-raised p-3"
              >
                <Laptop size={18} className="shrink-0 text-content-muted" />
                <div className="min-w-[12rem] flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{agent.deviceName}</span>
                    <span
                      className={clsx(
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                        agent.connected
                          ? 'bg-success/15 text-success'
                          : agent.status === 'REVOKED'
                            ? 'bg-danger/15 text-danger'
                            : 'bg-surface-sunken text-content-muted',
                      )}
                    >
                      {agent.connected ? 'Connected' : agent.status}
                    </span>
                  </div>
                  <div className="text-xs text-content-muted">
                    {agent.platform} · {agent.architecture} · agent {agent.agentVersion}
                  </div>
                </div>
                {agent.status !== 'REVOKED' && (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => void api.disconnectAgent(agent.id).then(reload)}
                      className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-sunken"
                      title="Disconnect this device now"
                    >
                      <Plug size={12} /> Disconnect
                    </button>
                    {confirmingRemove === agent.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setConfirmingRemove(null)}
                          className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-sunken"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmingRemove(null)
                            void api.revokeAgent(agent.id).then(reload)
                          }}
                          className="rounded-md bg-danger px-2 py-1 text-xs font-medium text-white"
                        >
                          Remove permanently
                        </button>
                      </>
                    ) : (
                      // Two steps, because this cannot be undone and sits
                      // beside Disconnect, which can.
                      <button
                        type="button"
                        onClick={() => setConfirmingRemove(agent.id)}
                        className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-danger hover:bg-danger/10"
                        title="Remove this device permanently"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

function ModelsTab() {
  const [models, setModels] = useState<ModelEntry[]>([])
  useEffect(() => {
    void api.listModels().then(setModels)
  }, [])

  return (
    <Panel
      title="Available models"
      description="Providers are configured on the server. Only those with valid credentials appear here."
    >
      {models.length === 0 ? (
        <p className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
          No AI provider is configured. Set an API key such as <code>ANTHROPIC_API_KEY</code> on the backend.
        </p>
      ) : (
        <ul className="space-y-1">
          {models.map((model) => (
            <li
              key={`${model.provider}-${model.id}`}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm"
            >
              <span className="font-mono">{model.id}</span>
              <span className="text-xs text-content-muted">{model.provider}</span>
              {model.kind === 'LOCAL' && !model.ready && (
                <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] text-content-muted">
                  not downloaded
                </span>
              )}
              {model.isDefault && (
                <span className="ml-auto rounded bg-accent/15 px-1.5 py-0.5 text-[10px] text-accent">
                  default
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

function MemoryTab() {
  const [memories, setMemories] = useState<Memory[]>([])
  const [key, setKey] = useState('')
  const [value, setValue] = useState('')

  const reload = () => void api.listMemories().then(setMemories)
  useEffect(reload, [])

  return (
    <Panel
      title="Saved preferences"
      description="NevTan reads these at the start of every conversation."
    >
      <div className="flex flex-wrap gap-2">
        <input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="preferred-java-version"
          className="w-full rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:border-accent sm:w-1/3"
        />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Java 21 for all new Spring Boot projects"
          className="w-full flex-1 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:border-accent sm:w-auto"
        />
        <button
          type="button"
          onClick={async () => {
            if (!key.trim() || !value.trim()) return
            await api.saveMemory(key.trim(), value.trim())
            setKey('')
            setValue('')
            reload()
          }}
          className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-content transition hover:opacity-90 sm:w-auto"
        >
          Save
        </button>
      </div>

      {memories.length === 0 ? (
        <p className="mt-4 text-sm text-content-muted">Nothing saved yet.</p>
      ) : (
        <ul className="mt-4 space-y-1">
          {memories.map((memory) => (
            <li
              key={memory.id}
              className="flex items-start gap-3 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm"
            >
              <span className="font-mono text-xs text-content-muted">{memory.key}</span>
              <span className="flex-1">{memory.value}</span>
              <button
                type="button"
                onClick={() => void api.deleteMemory(memory.id).then(reload)}
                className="text-content-muted hover:text-danger"
                aria-label={`Delete ${memory.key}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

const RISK_TONE = {
  LOW: 'bg-surface-sunken text-content-muted',
  MEDIUM: 'bg-warning/15 text-warning',
  HIGH: 'bg-danger/15 text-danger',
} as const

function ToolsTab() {
  const [tools, setTools] = useState<ToolInfo[]>([])
  useEffect(() => {
    void api.listTools().then(setTools)
  }, [])

  const groups: [ToolInfo['permission'], string][] = [
    ['LOW', 'Runs automatically — read-only inspection'],
    ['MEDIUM', 'Asks for approval — changes your system but is reversible'],
    ['HIGH', 'Always asks — destructive or privileged'],
  ]

  return (
    <div className="space-y-6">
      {groups.map(([level, description]) => (
        <section
          key={level}
          className="rounded-xl border border-border bg-surface p-4 sm:p-5"
        >
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className={clsx('rounded px-1.5 py-0.5 text-[10px] font-bold', RISK_TONE[level])}>
              {level}
            </span>
            <span className="text-[13px] text-content-muted">{description}</span>
          </div>
          <ul className="space-y-1">
            {tools
              .filter((tool) => tool.permission === level)
              .map((tool) => (
                <li
                  key={tool.name}
                  className="rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{tool.name}</span>
                    {!tool.localAgent && (
                      <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] text-content-muted">
                        server-side
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-content-muted">{tool.description}</p>
                </li>
              ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
